import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, RefreshCw, ChevronDown, Clock, MessageSquare, Inbox,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";

/**
 * Manager-facing section on the TimeClock page.
 *
 * Lists pending missed-punch requests at the top (with approve/reject
 * buttons) and keeps a collapsible section for the recently-reviewed
 * history. Approving writes a real entry into the timeclock, so the
 * standard per-staff log / weekly hours / payroll all pick it up
 * automatically.
 */
export default function MissedPunchManager({ onAfterAction }) {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [dialog, setDialog] = useState(null); // {req, action: "approve"|"reject"}
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/timeclock-requests?status=pending&limit=50"),
        api.get("/timeclock-requests?limit=25"),
      ]);
      setPending(p.data?.items || []);
      const all = r.data?.items || [];
      setReviewed(all.filter((x) => x.status !== "pending"));
    } catch {
      // silent — manager sees empty state
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = (req, action) => {
    setNote("");
    setDialog({ req, action });
  };

  const submit = async () => {
    if (!dialog) return;
    const { req, action } = dialog;
    setSubmitting(true);
    try {
      await api.post(`/timeclock-requests/${req.request_id}/${action}`, { note });
      toast.success(action === "approve" ? "Punch approved" : "Request rejected");
      setDialog(null);
      await load();
      if (typeof onAfterAction === "function") onAfterAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

  const calcHours = (req) => {
    if (!req.requested_clock_out) return null;
    try {
      const s = new Date(req.requested_clock_in).getTime();
      const e = new Date(req.requested_clock_out).getTime();
      const diff = (e - s) / 3600000;
      return diff > 0 ? diff.toFixed(2) : null;
    } catch { return null; }
  };

  const Row = ({ req, kind }) => {
    const hours = calcHours(req);
    const approved = req.status === "approved";
    const rejected = req.status === "rejected";
    return (
      <div
        data-testid={`missed-punch-row-${req.request_id}`}
        className="border border-slate-100 rounded-xl p-4 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ backgroundColor: req.user_color || "#64748B" }}>
              {(req.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 font-['Manrope']">{req.user_name}</p>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-xs text-slate-600 font-['Manrope']">
                  {fmt(req.requested_clock_in)}
                  {req.requested_clock_out && <>
                    <span className="mx-1 text-slate-300">→</span>
                    {fmt(req.requested_clock_out)}
                  </>}
                </span>
                {hours && (
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {hours}h
                  </span>
                )}
                {req.location_name && (
                  <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">
                    {req.location_name}
                  </span>
                )}
              </div>
              {req.reason && (
                <p className="text-xs text-slate-500 mt-1.5 font-['Manrope'] flex items-start gap-1">
                  <MessageSquare className="w-3 h-3 text-slate-300 mt-0.5 flex-shrink-0" />
                  <span>{req.reason}</span>
                </p>
              )}
              {kind === "reviewed" && req.reviewer_note && (
                <p className="text-[11px] text-slate-400 mt-1 italic font-['Manrope']">
                  “{req.reviewer_note}” — {req.reviewer_name || "manager"}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {kind === "pending" ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openDialog(req, "reject")}
                  data-testid={`reject-btn-${req.request_id}`}
                  className="h-8 border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-200 rounded-full px-3 text-xs">
                  <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => openDialog(req, "approve")}
                  data-testid={`approve-btn-${req.request_id}`}
                  className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-3 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                </Button>
              </>
            ) : (
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                  approved ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  : rejected ? "bg-rose-50 text-rose-700 border border-rose-100"
                  : "bg-slate-100 text-slate-500"
                }`}>
                {req.status}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="missed-punch-manager"
      className="bg-white border border-slate-100 rounded-2xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Inbox className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Missed Punch Requests</h2>
          {pending.length > 0 && (
            <span
              data-testid="missed-punch-pending-badge"
              className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
              {pending.length} pending
            </span>
          )}
        </div>
        <Button
          variant="ghost" size="sm" onClick={load}
          data-testid="refresh-missed-punches"
          className="text-slate-400 hover:text-slate-600 h-8 px-2 rounded-full">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {loading ? (
        <div className="h-10 bg-slate-50 rounded animate-pulse" />
      ) : pending.length === 0 && reviewed.length === 0 ? (
        <div className="text-center py-6">
          <Inbox className="w-8 h-8 text-slate-200 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate-400 font-['Manrope']">No missed-punch requests</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-2" data-testid="missed-punch-pending-list">
              {pending.map((req) => <Row key={req.request_id} req={req} kind="pending" />)}
            </div>
          )}

          {pending.length === 0 && (
            <p className="text-sm text-slate-400 font-['Manrope'] py-2">All caught up — no pending requests.</p>
          )}

          {reviewed.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowHistory((v) => !v)}
                data-testid="toggle-missed-punch-history"
                className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-colors py-1.5 font-['Manrope']">
                {showHistory ? "Hide" : "Show"} recent history ({reviewed.length})
                <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? "rotate-180" : ""}`} />
              </button>
              {showHistory && (
                <div className="space-y-2 mt-2" data-testid="missed-punch-history-list">
                  {reviewed.map((req) => <Row key={req.request_id} req={req} kind="reviewed" />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-[440px] bg-white rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">
              {dialog?.action === "approve" ? "Approve missed punch?" : "Reject request?"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <p className="text-sm font-semibold text-slate-800 font-['Manrope']">{dialog.req.user_name}</p>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">
                  {fmt(dialog.req.requested_clock_in)}
                  {dialog.req.requested_clock_out && <> → {fmt(dialog.req.requested_clock_out)}</>}
                </p>
                {dialog.req.reason && (
                  <p className="text-xs text-slate-500 mt-2 italic">“{dialog.req.reason}”</p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">
                  Note (optional){dialog.action === "reject" ? ", tell them why" : ""}
                </label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  data-testid="review-note"
                  placeholder={dialog.action === "approve"
                    ? "e.g. verified with schedule"
                    : "e.g. shift wasn't scheduled"}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none outline-none focus:border-rose-300" />
              </div>
              {dialog.action === "approve" && (
                <p className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 font-['Manrope']">
                  Approving will add this as a real TimeClock entry and count toward weekly hours & payroll.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialog(null)}
              data-testid="review-cancel"
              className="border-slate-200 rounded-full h-10">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="review-confirm"
              className={`rounded-full h-10 text-white ${
                dialog?.action === "approve" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"
              }`}>
              {submitting ? "Saving…" : dialog?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
